import {
  EJustification,
  ESectionInfoType,
  EVariableType,
  SUBMODEL_PREFIX,
} from '../data/Constants';
import {
  IAssignmentItem,
  IConfigurationValue,
  IConfigurationVariable,
  IExtendedConfigureResponse,
  IProductAssignments,
  ISection,
  ISectionAssignments,
} from '../../types';
import { getViewIdSuffix, getMultiNumericCode } from './ConfigurationDataHelperFunctions';
import { isScope } from './SummaryHelperFunction';
import { getVariableSalesText } from './SalesTextHelperFunctions';

/**
 * Helper function to extract product ID from section ID by finding parts that end with viewIdSuffix
 * @description Parses section ID to find product identifiers by splitting on dots and filtering
 * for parts ending with the view ID suffix, then removes the suffix to get clean product ID
 * @param {string} sectionId - The complete section identifier (e.g., "productId.sectionName_CPQ")
 * @param {string} viewIdSuffix - The view identifier suffix to match against (e.g., "_CPQ")
 * @returns {string | undefined} The extracted product ID without suffix, or undefined if not found
 * @example
 * extractProductId("123.456_CPQ.789", "_CPQ") // returns "456"
 * extractProductId("123.456.789", "_CPQ") // returns undefined
 */
const extractProductId = (sectionId: string, viewIdSuffix: string): string | undefined => {
  return sectionId
    .split('.')
    .filter((sIdPart) => sIdPart.endsWith(viewIdSuffix))
    .map((sIdPart) => sIdPart.replace(viewIdSuffix, ''))
    .pop();
};

/**
 * Filters configuration variable values to find those assigned by rule
 * @description Retrieves only the values that are both assigned and specifically assigned by 'Rule'
 * rather than by system rules or other automated processes
 * @param {IConfigurationVariable} variable - The configuration variable containing values to filter
 * @returns {IConfigurationValue[]} Array of values that are assigned by rule
 * @example
 * getValuesAssignedByRule(variable) // returns [{ value: "option1", state: { isAssigned: true, assigned: "Rule" } }]
 */
function getValuesAssignedByRule(variable: IConfigurationVariable): IConfigurationValue[] {
  return variable.values.filter((v) => {
    const isRuleAssigned = v.assigned === EJustification.Rule;

    if (variable.variableType === EVariableType.Number) {
      return isRuleAssigned && Number(v.value) > 0;
    }

    return isRuleAssigned;
  });
}

/**
 * Adds an assignment item to the section array if it doesn't already exist
 * @param {IAssignmentItem[]} sectionArray - Array to add the item to
 * @param {string} featureId - The feature ID
 * @param {string} optionId - The option ID
 * @param {string} name - The display name
 * @returns {void}
 */
function addAssignmentInSectionArray(
  sectionArray: IAssignmentItem[],
  featureId: string,
  optionId: string,
  name: string
): void {
  sectionArray.push({ featureId, optionId, name });
}

/**
 * Processes Number/String variable types and adds them to assignments
 * @param {IConfigurationVariable} variable - The variable to process
 * @param {string} variableId - The extracted variable ID
 * @param {IConfigurationValue[]} assignedValues - Values assigned by rule
 * @param {IAssignmentItem[]} sectionArray - Target array for assignments
 * @param {ISection} section - The section containing the variable (to check if multi-numeric)
 * @returns {void}
 */
function processNumericOrStringAssignments(
  variable: IConfigurationVariable,
  variableId: string,
  assignedValues: IConfigurationValue[],
  sectionArray: IAssignmentItem[],
  section: ISection
): void {
  let featureId = getVariableId(variableId);
  // Check if this specific section (not just its children) is multi-numeric by examining its own ID
  assignedValues.forEach((ruleValue) => {
    const displayValue = ruleValue.value ?? '';

    let optionId = `${featureId} (${displayValue})`;
    const name = variable.shortSalesText ?? variable.name; // Default name to variable name or ShortSalesText

    if (section.type === ESectionInfoType.MultiNumeric) {
      featureId = getMultiNumericCode(section.id);
    } else if (variable.variableType === EVariableType.String) {
      optionId = String(displayValue);
    }

    // Check if this feature ID already exists in the section array
    const featureExist = sectionArray.some((s) => s.featureId === featureId);
    addAssignmentInSectionArray(
      sectionArray,
      featureExist ? getHierarchicalVariableId(featureId) : featureId, // If feature already exists, show hierarchical ID which contains submodel paths; otherwise, show feature ID
      optionId,
      name ?? ''
    );
  });
}

/**
 * Processes Singleselect/Multiselect variable types and adds them to assignments
 * @param {string} variableId - The extracted variable ID
 * @param {IConfigurationValue[]} assignedValues - Values assigned by rule
 * @param {IAssignmentItem[]} sectionArray - Target array for assignments
 * @returns {void}
 */
function processSingleOrMultiSelectAssignments(
  variableId: string,
  assignedValues: IConfigurationValue[],
  sectionArray: IAssignmentItem[]
): void {
  assignedValues.forEach((ruleValue) => {
    if (ruleValue.name && ruleValue.value !== undefined) {
      // Check if this option ID already exists in the section array
      const optionExist = sectionArray.some((s) => s.optionId === String(ruleValue.value));
      addAssignmentInSectionArray(
        sectionArray,
        optionExist ? getHierarchicalVariableId(variableId) : getVariableId(variableId), // If option already exists, show hierarchical ID which contains submodel paths; otherwise, show feature ID in feature coloumn
        String(ruleValue.value),
        ruleValue.shortSalesText ?? ruleValue.name
      );
    }
  });
}

/**
 * Processes a configuration variable and adds assigned rule values to the result collection
 * @description Analyzes variable type and processes assigned values accordingly:
 * - For Number/String types: Adds variable name and ID if any values are assigned by rules
 * - For Singleselect/Multiselect types: Adds individual option names and values for each assigned rule value
 * @param {IConfigurationVariable} variable - The configuration variable to process
 * @param {string} productKey - The product key in format "productId#productName"
 * @param {string} sectionName - The section name to group assignments
 * @param {IProductAssignments} assignmentsByRule - Collection to store processed rule variables by product and section
 * @param {ISection} section - The section containing the variable
 * @returns {void} Modifies the assignmentsByRule collection in place
 */
function processAssignmentsByRule(
  variable: IConfigurationVariable,
  productKey: string,
  sections: ISection[],
  assignmentsByRule: IProductAssignments,
  section: ISection
): void {
  const assignedByRuleValues = getValuesAssignedByRule(variable);
  if (assignedByRuleValues.length === 0) return;

  const sectionName =
    section.type === ESectionInfoType.MultiNumeric
      ? resolveSectionName(section, sections)
      : (section.name ?? '## Default ##');

  // Initialize section array if it doesn't exist
  if (!assignmentsByRule[productKey][sectionName]) {
    assignmentsByRule[productKey][sectionName] = [];
  }

  const sectionArray = assignmentsByRule[productKey][sectionName];
  const varType = variable.variableType;

  if (varType === EVariableType.Number || varType === EVariableType.String) {
    processNumericOrStringAssignments(
      variable,
      variable.id,
      assignedByRuleValues,
      sectionArray,
      section
    );
  } else if (varType === EVariableType.Singleselect || varType === EVariableType.Multiselect) {
    processSingleOrMultiSelectAssignments(variable.id, assignedByRuleValues, sectionArray);
  }
}

/**
 * Resolves the product name from configuration model context
 * @param {string} productId - The product ID to look up
 * @param {IExtendedConfigureResponse} configuration - The configuration state
 * @returns {string} The product name or productId as fallback
 */
function getProductName(productId: string, configuration: IExtendedConfigureResponse): string {
  const subModel = configuration?.savedConfiguration?.modelContext?.subModels?.find(
    (sm) => sm.id === productId
  );
  let name = subModel
    ? subModel.name
    : configuration?.savedConfiguration?.modelContext?.rootModel?.name;
  const id = subModel
    ? subModel.id
    : configuration?.savedConfiguration?.modelContext?.rootModel?.id;
  name = getVariableSalesText(configuration.shortSalesText, id) ?? name;

  return name ?? productId;
}

/**
 * Resolves the section name, using parent section name for multiNumeric types
 * @description For multi-numeric sections, returns the parent section's name instead of the child's name.
 * This ensures proper grouping of multi-numeric variables under their parent section heading.
 * @param {ISection} section - The section to resolve the name for
 * @param {ISection[]} allSections - All sections for parent lookup
 * @returns {string} The resolved section name (parent name for multi-numeric, or section name otherwise)
 * @example
 * // Given a multi-numeric section with hierarchical ID:
 * // section.id = "722223_CPQ.722350_CPQ.S722350_53.S722350_FCV0242_999"
 * // section.name = "FCV0894"
 * // Parent section (id: "95211_CPQ", name: "Configuration Options")
 * resolveSectionName(section, allSections)
 * // Returns: "Configuration Options" (parent section name)
 *
 * @example
 * // Given a regular section without parent:
 * // section.id = "95211_CPQ"
 * // section.name = "Main Settings"
 * resolveSectionName(section, allSections)
 * // Returns: "Main Settings" (own section name)
 */
function resolveSectionName(section: ISection, allSections: ISection[]): string {
  // Use section's own name as fallback, or a default if name is undefined
  const defaultName = section.name ?? '## Multi Numeric Default ##';

  // Split section ID by dots to determine hierarchy level
  // Example: "722223_CPQ.722350_CPQ.S722350_53.S722350_FCV0242_999" becomes ["722223_CPQ", "722350_CPQ", "S722350_53", "S722350_FCV0242_999"]
  const idParts = section.id.split('.');

  // If only one part exists, this is a root section with no parent
  if (idParts.length <= 1) {
    return defaultName;
  }

  // Reconstruct parent ID by removing the last segment
  // Example: ["722223_CPQ", "722350_CPQ", "S722350_53", "S722350_FCV0242_999"] becomes "722223_CPQ.722350_CPQ.S722350_53"
  const parentId = idParts.slice(0, -1).join('.');

  // Find parent section in the full sections array by matching IDs
  const parentSection = allSections.find((sec) => sec.id === parentId);

  // Return parent's name if found, otherwise fall back to section's own name
  return parentSection?.name ?? defaultName;
}

/**
 * Checks if a section should be processed for rule assignments
 * @param {ISection} section - The section to check
 * @returns {boolean} True if section should be processed
 */
function shouldProcessSection(section: ISection): boolean {
  return !section.isItemsAvailable && !isScope(section);
}

/**
 * Creates a collection of variables assigned by rules, organized by product and section
 * @description Processes all sections to extract variables that have been assigned by user rules,
 * grouping them by their associated product IDs and section names. Only processes sections that are available
 * for configuration (not scope sections or unavailable items).
 * @param {ISection[]} sections - Array of configuration sections to process
 * @param {IExtendedConfigureResponse} configuration - The configuration state containing model context
 * @returns {Record<string, Record<string, Array<{ id: string; name: string; featureid: string }>>>}
 * Object mapping "productId (productName)" to section names, which map to arrays of assigned rule variables
 * @example
 * getAssignmentsByRule(sections, configuration)
 * // Returns: {
 * //   "product123 (ProductName)": {
 * //     "Section1": [
 * //       { id: "opt1", name: "Option1", featureid: "var1" },
 * //       { id: "", name: "Variable2", featureid: "var2" }
 * //     ],
 * //     "Section2": [...]
 * //   },
 * //   "product456 (ProductName2)": {...}
 * // }
 */
export const getAssignmentsByRule = (
  sections: ISection[],
  configuration: IExtendedConfigureResponse
): Record<string, any> => {
  const assignmentsByRule: Record<string, any> = {};
  const viewIdSuffix = getViewIdSuffix();

  sections.forEach((section: ISection) => {
    const productId = extractProductId(section.id, viewIdSuffix);
    if (!productId) return;

    const productName = getProductName(productId, configuration);
    const productKey = `${productId} (${productName})`;

    // Initialize product key if needed
    if (!assignmentsByRule[productKey]) {
      assignmentsByRule[productKey] = {};
    }

    if (shouldProcessSection(section)) {
      section.variables.forEach((variable) => {
        processAssignmentsByRule(variable, productKey, sections, assignmentsByRule, section);
      });
    }
  });

  return assignmentsByRule;
};

/**
 * Compares two arrays and returns items that exist in current but not in previous
 * @param {IAssignmentItem[]} currentArray - Current array
 * @param {IAssignmentItem[]} previousArray - Previous array
 * @returns {IAssignmentItem[]} Array of newly added items
 */
function getArrayDifferences(
  currentArray: IAssignmentItem[],
  previousArray: IAssignmentItem[]
): IAssignmentItem[] {
  // Quick check if arrays are identical
  if (JSON.stringify(currentArray) === JSON.stringify(previousArray)) {
    return [];
  }

  // Find items in current that don't exist in previous
  const previousSet = new Set(previousArray.map((item) => JSON.stringify(item)));
  return currentArray.filter((item) => !previousSet.has(JSON.stringify(item)));
}

/**
 * Processes a single section comparison and updates differences
 * @param {IAssignmentItem[]} currentArray - Current section assignments
 * @param {IAssignmentItem[]} previousArray - Previous section assignments
 * @param {string} productKey - Product key for error logging
 * @param {string} sectionName - Section name for error logging
 * @returns {{ items: IAssignmentItem[]; count: number }} Added items and their count
 */
function processSectionDifferences(
  currentArray: IAssignmentItem[],
  previousArray: IAssignmentItem[],
  productKey: string,
  sectionName: string
): { items: IAssignmentItem[]; count: number } {
  try {
    const addedItems = getArrayDifferences(currentArray, previousArray);
    return { items: addedItems, count: addedItems.length };
  } catch (error) {
    console.error(`Error comparing arrays for ${productKey} > ${sectionName}:`, error);
    // Fallback: treat entire current array as new
    return { items: currentArray, count: currentArray.length };
  }
}

/**
 * Adds section differences to the result if there are any changes
 * @param {IProductAssignments} differences - The differences accumulator
 * @param {string} productKey - The product key
 * @param {string} sectionName - The section name
 * @param {IAssignmentItem[]} items - The items to add
 * @returns {void}
 */
function addSectionDifferences(
  differences: IProductAssignments,
  productKey: string,
  sectionName: string,
  items: IAssignmentItem[]
): void {
  if (items.length > 0) {
    if (!differences[productKey]) {
      differences[productKey] = {};
    }
    differences[productKey][sectionName] = items;
  }
}

/**
 * Processes all sections for a given product and returns differences
 * @param {ISectionAssignments} currentSections - Current sections for the product
 * @param {ISectionAssignments} previousSections - Previous sections for the product
 * @param {string} productKey - The product key
 * @param {IProductAssignments} differences - The differences accumulator
 * @returns {number} Total count of differences found
 */
function processProductSections(
  currentSections: ISectionAssignments,
  previousSections: ISectionAssignments,
  productKey: string,
  differences: IProductAssignments
): number {
  let count = 0;

  for (const sectionName of Object.keys(currentSections)) {
    const currentArray = Array.isArray(currentSections[sectionName])
      ? currentSections[sectionName]
      : [];
    const previousArray = Array.isArray(previousSections[sectionName])
      ? previousSections[sectionName]
      : [];

    const { items, count: sectionCount } = processSectionDifferences(
      currentArray,
      previousArray,
      productKey,
      sectionName
    );

    addSectionDifferences(differences, productKey, sectionName, items);
    count += sectionCount;
  }

  return count;
}

/**
 * Compare two currentAssignmentsByRule objects and return differences with count
 * @description Only tracks newly added items (current state changes), not removed items.
 * Handles nested structure: { "productId#productName": { "sectionName": [{ id, name, featureid }] } }
 * @param {IProductAssignments} previous - Previous currentAssignmentsByRule state
 * @param {IProductAssignments} current - Current currentAssignmentsByRule from getSelectableSections
 * @returns {{ differences: IProductAssignments; count: number }} - Object containing differences (added items only) and count
 */
export const getDeltaAssignmentsByRule = (
  previous: IProductAssignments,
  current: IProductAssignments
): { differences: IProductAssignments; count: number } => {
  const differences: IProductAssignments = {};
  const safePrevious = previous ?? {};
  const safeCurrent = current ?? {};
  let totalCount = 0;

  for (const productKey of Object.keys(safeCurrent)) {
    const currentSections = safeCurrent[productKey];

    if (!currentSections || typeof currentSections !== 'object') continue;

    const previousSections = safePrevious[productKey] ?? {};
    totalCount += processProductSections(
      currentSections,
      previousSections,
      productKey,
      differences
    );
  }

  return { differences, count: totalCount };
};

/**
 * Removes the submodel prefix from a variable ID to get the hierarchical identifier
 * @description Strips the 'I_' prefix from submodel variable IDs to create a clean hierarchical path
 * @param {string} variableId - The variable ID that may contain the submodel prefix
 * @returns {string} The variable ID with the submodel prefix removed
 * @example
 * getHierarchicalVariableId("I_722467.I_867030.APPLICATION_AREAS") // returns "722467.867030.APPLICATION_AREAS"
 * getHierarchicalVariableId("RegularVar") // returns "RegularVar"
 */
function getHierarchicalVariableId(variableId: string): string {
  return variableId.replaceAll(SUBMODEL_PREFIX, '');
}

/**
 * Extracts the last segment from a dot-separated variable ID
 * @description Splits the variable ID by dots and returns the final part, removing any hierarchical path prefixes
 * @param {string} variableId - The full variable ID with potential dot-separated path (e.g., "I_SubModel_1.Feature.Variable")
 * @returns {string} The last segment of the variable ID without the hierarchical path
 * @example
 * getVariableId("I_722467.I_867030.APPLICATION_AREAS") // returns "APPLICATION_AREAS"
 * getVariableId("SimpleVariable") // returns "SimpleVariable"
 */
function getVariableId(variableId: string): string {
  return variableId.split('.').pop()!;
}
